import { IRELAND } from '@nxsys/store/constants/keys';
import {
  EeErPensionMethodEnum,
  PayrollFeeMethodEnum,
} from '@nxsys/constants/AppEnums';

// ---------------------------------------------------------------------------
// Eircode (Ireland)
// ---------------------------------------------------------------------------

// Routing Key: letter (AC-FHKNPRTV-Y) + 2 digits, or special case D6W
// Unique Identifier: 4 alphanumeric chars (digits + valid letters)
// Excluded letters: B, G, I, J, L, M, O, Q, S, U, Z
// Reference: https://en.wikipedia.org/wiki/Postal_addresses_in_the_Republic_of_Ireland
const EIRCODE_PATTERN =
  /^(?:[AC-FHKNPRTV-Y][0-9]{2}|D6W) ?[0-9AC-FHKNPRTV-Y]{4}$/;

// ---------------------------------------------------------------------------
// UK Post Code
// ---------------------------------------------------------------------------

// Simplified format: 1-2 letters + digit + optional letter/digit + space + digit + 2 letters
// Matches backend regex to avoid FE/BE validation mismatch
// Special case: GIR 0AA (Santander UK, formerly Girobank)
const UK_POSTCODE_PATTERN =
  /^(GIR ?0AA|[A-Z]{1,2}\d[A-Z\d]? ?\d[A-Z]{2})$/;

// ---------------------------------------------------------------------------
// Unified validation & formatting
// ---------------------------------------------------------------------------

/**
 * Validate postcode/eircode.
 * @param value - The postcode/eircode value
 * @param payrollCountry - IRELAND → Eircode, otherwise → UK postcode
 */
export const validatePostCode = (
  value: string,
  payrollCountry: string,
): boolean => {
  if (!value || typeof value !== 'string') return false;
  const upper = value.toUpperCase();
  if (payrollCountry === IRELAND) return EIRCODE_PATTERN.test(upper);
  return UK_POSTCODE_PATTERN.test(upper);
};

/**
 * Format postcode/eircode with appropriate spacing.
 * Only inserts space when value is a complete valid postcode to avoid
 * corrupting intermediate input during typing (onChange fires per keystroke).
 */
export const formatPostCode = (
  value: string,
  payrollCountry: string,
): string => {
  if (!value || typeof value !== 'string') return '';
  const val = value.toUpperCase().replace(/\s/g, '');
  if (!validatePostCode(val, payrollCountry)) return val;
  if (payrollCountry === IRELAND) {
    return val.slice(0, 3) + ' ' + val.slice(3);
  }
  return val.slice(0, val.length - 3) + ' ' + val.slice(-3);
};

// ---------------------------------------------------------------------------
// Ant Design form validation rule
// ---------------------------------------------------------------------------

type Messages = Record<string, string>;

/**
 * Postcode/eircode validation rule for Ant Design Form.Item.
 * Shows "Invalid Eircode" for Ireland, "Invalid postcode" for UK.
 */
export const postCodeValidationRule = (
  messages: Messages,
  payrollCountry: string,
) => ({
  validator: async (_: any, value: string) => {
    if (!value) return;
    if (!validatePostCode(value, payrollCountry)) {
      const msg =
        payrollCountry === IRELAND
          ? messages['form.invalidEirCode']
          : messages['form.invalidPostcode'];
      throw new Error(msg || 'Invalid postcode');
    }
  },
});

// ---------------------------------------------------------------------------
// VAT registration number
// ---------------------------------------------------------------------------

// Separators are ignored because stored numbers carry them ("105 0169 61",
// "GB 123 4567 89", "123/4567/89").
// UK: 9 digits, or 12 for a branch trader, optionally prefixed GB.
const UK_VAT_REG_NO = /^(GB)?(\d{9}|\d{12})$/;
// Ireland: 7 digits + 1 or 2 letters, or the old 8-character form (IE8Z49289F).
const IE_VAT_REG_NO = /^IE(\d{7}[A-W][A-IW]?|\d[A-Z+*]\d{5}[A-W])$/;

/**
 * Strip separators and upper-case. The Irish field is pre-filled with "IE",
 * which on its own is no number, so it compacts to ''.
 */
export const compactVatRegNo = (value?: string): string => {
  const compact = (value ?? '').replace(/[\s/-]/g, '').toUpperCase();
  return compact === 'IE' ? '' : compact;
};

/**
 * VAT registration number rule for Ant Design Form.Item. Empty, or a bare "IE"
 * prefix, passes: the field is optional.
 */
export const vatRegNoValidationRule = (
  messages: Messages,
  payrollCountry: string,
) => ({
  validator: async (_: any, value: string) => {
    const compact = compactVatRegNo(value);
    if (!compact) return;
    const isIreland = payrollCountry === IRELAND;
    if (!(isIreland ? IE_VAT_REG_NO : UK_VAT_REG_NO).test(compact)) {
      throw new Error(
        isIreland
          ? messages['form.invalidIeVatRegNo'] || 'Invalid Irish VAT number'
          : messages['form.invalidUkVatRegNo'] || 'Invalid UK VAT number',
      );
    }
  },
});

// ---------------------------------------------------------------------------
// UK / Scottish (S) / Welsh (C) tax code
// ---------------------------------------------------------------------------

// Mirrors the backend validateTaxCode
// (nxsys-backend src/utils/validations/taxCodeValidation.ts) 1:1 so the FE and
// BE agree on what a valid code is — a divergence would let the FE block a code
// the BE accepts (or vice-versa). NX-5582: the Week1/Month1 (emergency) basis
// lives in the separate Tax Basis field, never embedded in the code, so an
// embedded W1/M1/X suffix (e.g. 1257LX) is malformed input and is rejected.
// Reference: https://www.gov.uk/tax-codes/what-your-tax-code-means
const TAX_CODE_K = /^K\d+$/;
const TAX_CODE_STANDARD = /^\d+[LMNT]$/;
const UK_TAX_SPECIAL = ['BR', 'D0', 'D1', 'NT', '0T'];
const WELSH_TAX_SPECIAL = ['BR', 'D0', 'D1', 'D2', '0T'];
const SCOTTISH_TAX_SPECIAL = ['BR', 'D0', 'D1', 'D2', 'D3', '0T'];

const matchesTaxCodeBody = (code: string, special: string[]): boolean =>
  special.includes(code) || TAX_CODE_K.test(code) || TAX_CODE_STANDARD.test(code);

/**
 * Validate a UK / Scottish (S) / Welsh (C) tax code. Case-insensitive.
 */
export const isValidTaxCode = (value: string): boolean => {
  if (!value || typeof value !== 'string') return false;
  const clean = value.trim().toUpperCase();
  if (clean.length === 0) return false;
  if (clean.startsWith('C')) {
    return matchesTaxCodeBody(clean.substring(1), WELSH_TAX_SPECIAL);
  }
  if (clean.startsWith('S')) {
    return matchesTaxCodeBody(clean.substring(1), SCOTTISH_TAX_SPECIAL);
  }
  return matchesTaxCodeBody(clean, UK_TAX_SPECIAL);
};

/**
 * Tax code validation rule for Ant Design Form.Item. Empty passes here (add a
 * separate `required` rule where the field is mandatory) so this only flags a
 * non-empty value that is not a recognisable tax code.
 */
export const taxCodeValidationRule = (messages: Messages = {}) => ({
  validator: async (_: any, value: string) => {
    if (!value) return;
    if (!isValidTaxCode(value)) {
      throw new Error(messages['form.invalidTaxCode'] || 'Invalid tax code');
    }
  },
});

// ---------------------------------------------------------------------------
// AWR notification weeks
// ---------------------------------------------------------------------------

/** The AWR qualifying period is 12 weeks, so a notice past week 12 never fires. */
const AWR_MAX_WEEK = 12;

/** Carried over from the classic UI, which capped the list at five notices. */
const AWR_MAX_NOTIFICATION_WEEKS = 5;

/**
 * Validate the comma-separated week list the form collects, before
 * getArrayFromComaSeparateString turns it into the number[] the API expects.
 * Returns the first broken rule's message, or null when the value is usable.
 * Empty passes — the field is optional and clearing it has to keep working.
 *
 * NX-5851: the new UI shipped this field with no validation at all. The rules
 * mirror validateNotificationWeeks in the classic UI, with two changes: a lower
 * bound of 1 (a trailing comma parses to week 0, which no payroll week matches)
 * and ordering folded together with uniqueness, since the backend enforces both
 * as one strictly-ascending rule.
 */
export const validateNotificationWeeks = (
  value: string,
  messages: Messages = {},
): string | null => {
  if (!value) return null;

  const parts = value.split(',').map((part) => part.trim());
  if (parts.some((part) => !/^\d+$/.test(part))) {
    return (
      messages['form.notificationWeeksNumeric'] ||
      'Weeks must be numbers separated by commas'
    );
  }

  const weeks = parts.map(Number);
  if (weeks.some((week) => week < 1 || week > AWR_MAX_WEEK)) {
    return (
      messages['form.notificationWeeksRange'] ||
      `Weeks must be between 1 and ${AWR_MAX_WEEK}`
    );
  }
  if (weeks.length > AWR_MAX_NOTIFICATION_WEEKS) {
    return (
      messages['form.notificationWeeksCount'] ||
      `A maximum of ${AWR_MAX_NOTIFICATION_WEEKS} weeks can be entered`
    );
  }
  // The notice email renders the entry after the current one as {{nextAWRWeek}},
  // so an unordered list names the wrong next week and a repeated one names
  // itself.
  if (weeks.some((week, index) => index > 0 && week <= weeks[index - 1])) {
    return (
      messages['form.notificationWeeksOrder'] ||
      'Weeks must be in ascending order, with no duplicates'
    );
  }

  return null;
};

/**
 * Notification weeks rule for Ant Design Form.Item. Shared by the company
 * defaults tab and the create wizard so the two cannot drift apart.
 */
export const notificationWeeksValidationRule = (messages: Messages = {}) => ({
  validator: async (_: any, value: string) => {
    const error = validateNotificationWeeks(value, messages);
    if (error) {
      throw new Error(error);
    }
  },
});

// ---------------------------------------------------------------------------
// Invoice settings (NX-3024)
// ---------------------------------------------------------------------------

// Shared by Employer › Invoice Settings, Agency › Payroll & Finance and the
// Raise ER Cost modal, which hold the same invoice setting fields.

type FieldGetter = { getFieldValue: (name: any) => any };

/** These forms keep a select's value as the option object; a few as the bare value. */
const selectedValue = (value: unknown) =>
  value !== null && typeof value === 'object'
    ? (value as { value?: unknown }).value
    : value;

/**
 * Validate the due day against its rule. "Day(s) after the invoice date" takes
 * any count from 0; the other rules name a day of the month, so 1-31, the
 * range the backend enforces on settings save. Empty passes: the field has its
 * own `required` rule.
 */
export const validateInvoiceDueDate = (
  value: unknown,
  rule: unknown,
  messages: Messages = {},
): string | null => {
  if (value === undefined || value === null || value === '') return null;
  const day = Number(value);
  if (selectedValue(rule) === 'DAYS_AFTER_THE_INVOICE_DATE') {
    return Number.isNaN(day) || day < 0
      ? messages['invoices.dueDaysNotNegative'] ||
          'Number of days cannot be negative'
      : null;
  }
  return Number.isNaN(day) || day < 1 || day > 31
    ? messages['invoices.dueDayOfMonth'] ||
        'Due Date must be a day of the month, between 1 and 31'
    : null;
};

/** Due date rule for Ant Design Form.Item. Pair it with `dependencies` on the rule field. */
export const invoiceDueDateRule =
  (messages: Messages = {}, ruleName: any = 'rule') =>
  ({ getFieldValue }: FieldGetter) => ({
    validator: async (_: any, value: unknown) => {
      const error = validateInvoiceDueDate(
        value,
        getFieldValue(ruleName),
        messages,
      );
      if (error) {
        throw new Error(error);
      }
    },
  });

/**
 * A PEO invoice raised after the draft payroll can only charge a fixed fee per
 * employee; the backend refuses a tiered fee when that invoice is raised.
 */
export const isPEOAfterDraftPayroll = (
  raiseInvoicePEO: unknown,
  invoiceRaisedAfter: unknown,
): boolean =>
  !!raiseInvoicePEO &&
  selectedValue(invoiceRaisedAfter) === 'AFTER_DRAFT_PAYROLL';

/**
 * The payroll fee method options with Tiered fee disabled after the draft
 * payroll. It stays listed, so a Tiered fee saved before still shows its label.
 */
export const payrollFeeMethodOptions = <T extends { value: unknown }>(
  options: T[],
  raiseInvoicePEO: unknown,
  invoiceRaisedAfter: unknown,
) => {
  const afterDraft = isPEOAfterDraftPayroll(raiseInvoicePEO, invoiceRaisedAfter);
  return options.map((option) => ({
    ...option,
    disabled: afterDraft && option.value === PayrollFeeMethodEnum.TIERED_FEE,
  }));
};

export const validateAfterDraftFeeMethod = (
  method: unknown,
  raiseInvoicePEO: unknown,
  invoiceRaisedAfter: unknown,
  messages: Messages = {},
): string | null =>
  selectedValue(method) === PayrollFeeMethodEnum.TIERED_FEE &&
  isPEOAfterDraftPayroll(raiseInvoicePEO, invoiceRaisedAfter)
    ? messages['invoices.onlyFixedFeeAfterDraftPayroll'] ||
      'Only Fixed Fee per employee is allowed when the invoice is raised after draft payroll'
    : null;

/**
 * Payroll fee method rule for Ant Design Form.Item. Pair it with
 * `dependencies` on the two fields it reads.
 */
export const afterDraftFeeMethodRule =
  (messages: Messages, raiseInvoicePEOName: any, invoiceRaisedAfterName: any) =>
  ({ getFieldValue }: FieldGetter) => ({
    validator: async (_: any, value: unknown) => {
      const error = validateAfterDraftFeeMethod(
        value,
        getFieldValue(raiseInvoicePEOName),
        getFieldValue(invoiceRaisedAfterName),
        messages,
      );
      if (error) {
        throw new Error(error);
      }
    },
  });

/** A tiered fee needs at least one tier; the backend refuses an empty table. */
export const validateTieredFees = (
  method: unknown,
  tieredFees: unknown,
  messages: Messages = {},
): string | null =>
  selectedValue(method) === PayrollFeeMethodEnum.TIERED_FEE &&
  !(Array.isArray(tieredFees) && tieredFees.length > 0)
    ? messages['invoices.tieredFeeRequired'] ||
      'At least one tiered fee is required'
    : null;

/** Tiered fee rule for the payroll fee method Form.Item. */
export const tieredFeesRule =
  (messages: Messages = {}, tieredFeesName: any = 'tieredFees') =>
  ({ getFieldValue }: FieldGetter) => ({
    validator: async (_: any, value: unknown) => {
      const error = validateTieredFees(
        value,
        getFieldValue(tieredFeesName),
        messages,
      );
      if (error) {
        throw new Error(error);
      }
    },
  });

// ---------------------------------------------------------------------------
// Ireland private pension contributions (NX-6174)
// ---------------------------------------------------------------------------

/**
 * A percentage contribution must sit between 0% and 100%, and a fixed amount
 * cannot be negative. Without this a 250% deduction drove net pay negative and
 * a -5% one was added to net pay. Empty passes: the forms default it to 0.
 */
export const validatePrivatePensionValue = (
  value: unknown,
  type: unknown,
  messages: Messages = {},
): string | null => {
  if (value === undefined || value === null || value === '') return null;
  const num = Number(value);
  if (selectedValue(type) === EeErPensionMethodEnum.AMOUNT) {
    return Number.isNaN(num) || num < 0
      ? messages['form.pensionAmountNotNegative'] ||
          'Amount cannot be negative'
      : null;
  }
  return Number.isNaN(num) || num < 0 || num > 100
    ? messages['form.pensionPercentageRange'] ||
        'Please enter a value between 0% and 100%'
    : null;
};

/**
 * Contribution value rule for Ant Design Form.Item, shared by the employer
 * scheme form and the employee add/edit modals. Pair it with `dependencies` on
 * the type field so switching between % and amount re-checks the value.
 */
export const privatePensionValueRule =
  (typeName: string, messages: Messages = {}) =>
  ({ getFieldValue }: FieldGetter) => ({
    validator: async (_: any, value: unknown) => {
      const error = validatePrivatePensionValue(
        value,
        getFieldValue(typeName),
        messages,
      );
      if (error) {
        throw new Error(error);
      }
    },
  });
